import { useState, useEffect } from 'react';
import { CurrentEmailVerification } from './CurrentEmailVerification';
import { NewEmailInput } from './NewEmailInput';
import { EmailChangeConfirmation } from './EmailChangeConfirmation';

interface EmailChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentEmail: string;
  userId: string;
  userName: string;
  onSuccess: () => void;
}

type EmailChangeStep = 'verify_current' | 'input_new' | 'confirm_change' | 'completed';

export function EmailChangeModal({ 
  isOpen, 
  onClose, 
  currentEmail, 
  userId,
  userName,
  onSuccess 
}: EmailChangeModalProps) {
  const [currentStep, setCurrentStep] = useState<EmailChangeStep>('verify_current');
  const [newEmail, setNewEmail] = useState('');
  const [isCurrentEmailVerified, setIsCurrentEmailVerified] = useState(false);
  const [isNewEmailVerified, setIsNewEmailVerified] = useState(false);

  // 모달이 열릴 때마다 상태 초기화
  useEffect(() => {
    if (isOpen) {
      setCurrentStep('verify_current');
      setNewEmail('');
      setIsCurrentEmailVerified(false);
      setIsNewEmailVerified(false);
    }
  }, [isOpen]);


  // 모달 외부 클릭 시 닫기 방지 (body overflow 제어)
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleCurrentEmailVerified = () => {
    setIsCurrentEmailVerified(true);
    setCurrentStep('input_new');
  };

  const handleNewEmailVerified = (email: string) => {
    setNewEmail(email);
    setIsNewEmailVerified(true);
    setCurrentStep('confirm_change');
  };

  const handleEmailChangeCompleted = () => {
    setCurrentStep('completed');
    onSuccess();
    // 2초 후 모달 닫기
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  const handleClose = () => {
    if (currentStep !== 'completed') {
      onClose();
    }
  };

  const handleBackToCurrentVerification = () => {
    setCurrentStep('verify_current');
    setIsCurrentEmailVerified(false);
  };

  const handleBackToNewInput = () => {
    setCurrentStep('input_new');
    setIsNewEmailVerified(false);
    setNewEmail('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div 
        className="relative bg-white rounded-xl w-full max-w-[400px] lg:max-w-[600px] shadow-xl max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* 헤더 */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900">
              이메일 변경
            </h3>
            {currentStep !== 'completed' && (
              <button
                onClick={handleClose}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* 진행 상태 표시 */}
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep === 'verify_current' ? 'bg-[#4CD964] text-white' : 
                isCurrentEmailVerified ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-500'
              }`}>
                1
              </div>
              <div className={`w-12 h-1 ${isCurrentEmailVerified ? 'bg-green-200' : 'bg-gray-200'}`}></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep === 'input_new' ? 'bg-[#4CD964] text-white' : 
                isNewEmailVerified ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-500'
              }`}>
                2
              </div>
              <div className={`w-12 h-1 ${isNewEmailVerified ? 'bg-green-200' : 'bg-gray-200'}`}></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep === 'confirm_change' || currentStep === 'completed' ? 'bg-[#4CD964] text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                3
              </div>
            </div>
          </div>

          {/* 단계별 컴포넌트 */}
          {currentStep === 'verify_current' && (
            <CurrentEmailVerification
              currentEmail={currentEmail}
              userName={userName}
              onVerified={handleCurrentEmailVerified}
              onCancel={handleClose}
            />
          )}

          {currentStep === 'input_new' && (
            <NewEmailInput
              currentEmail={currentEmail}
              userName={userName}
              onVerified={handleNewEmailVerified}
              onCancel={handleClose}
              onBack={handleBackToCurrentVerification}
            />
          )}

          {currentStep === 'confirm_change' && (
            <EmailChangeConfirmation
              currentEmail={currentEmail}
              newEmail={newEmail}
              userId={userId}
              onCompleted={handleEmailChangeCompleted}
              onCancel={handleClose}
              onBack={handleBackToNewInput}
            />
          )}

          {currentStep === 'completed' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                이메일이 변경되었습니다!
              </h4>
              <p className="text-sm text-gray-600">
                새로운 이메일 주소로 로그인해주세요.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}